import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import {
  CalendarDays,
  CheckSquare,
  Hash,
  ListFilter,
  SlidersHorizontal,
  Type,
} from 'lucide-react';
import type {
  CardCustomField,
  CardDetail,
  CustomFieldValuePatchBody,
  LocalePreferences,
} from '@molt/shared';
import { Button, EmptyState, TextField } from '@/components';
import {
  dateTimeLocalInZoneToIso,
  formatDateTimeLocalInZone,
  formatLocaleCardDate,
  type LocaleFormattingTranslate,
} from '@/lib/locale-formatting';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import {
  useClearCardCustomFieldValue,
  useUpdateCardCustomFieldValue,
} from '@/lib/custom-fields';
import { customFieldTypeLabel } from '@/lib/custom-field-type';
import { cn } from '@/lib/cn';

export interface CardCustomFieldsSectionProps {
  card: CardDetail;
  boardId: string;
  canEdit: boolean;
}

export function CardCustomFieldsSection({
  card,
  boardId,
  canEdit,
}: CardCustomFieldsSectionProps): ReactElement {
  // ⚠ The PARENT had no `useInterfaceLanguage()` before this slice - only
  // `CustomFieldControl` did - and the heading and both empty-state strings
  // live up here, which is why finding 3 of the card-dates return undercounted
  // this file's English by reading the child's hook as the whole surface.
  const { t } = useInterfaceLanguage();
  const fields = useMemo(
    () => card.customFields.filter((field) => !field.hidden),
    [card.customFields],
  );

  return (
    <section aria-labelledby="card-custom-fields-heading" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-custom-fields-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.customFields.heading')}
        </h2>
      </div>

      {fields.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          title={t('cardBack.customFields.emptyTitle')}
          body={t('cardBack.customFields.emptyBody')}
        />
      ) : (
        <div className="grid gap-2">
          {fields.map((field) => (
            <CustomFieldControl
              key={field.id}
              cardId={card.id}
              boardId={boardId}
              field={field}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CustomFieldControl({
  cardId,
  boardId,
  field,
  canEdit,
}: {
  cardId: string;
  boardId: string;
  field: CardCustomField;
  canEdit: boolean;
}): ReactElement {
  const localePreferences = useLocalePreferencesValue();
  const { t } = useInterfaceLanguage();
  const updateValue = useUpdateCardCustomFieldValue();
  const clearValue = useClearCardCustomFieldValue();
  const [draft, setDraft] = useState(() => draftFromField(field, localePreferences));
  const [error, setError] = useState<string | null>(null);
  const currentLabel = valueLabel(t, field, localePreferences);
  const hasValue = field.value !== null;
  const busy = updateValue.isPending || clearValue.isPending;

  useEffect(() => {
    setDraft(draftFromField(field, localePreferences));
    setError(null);
  }, [field, localePreferences]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canEdit || busy) return;
    setError(null);

    const patch = patchFromDraft(t, field, draft, localePreferences);
    if (patch === null) {
      await clearValue.mutateAsync({ cardId, boardId, fieldId: field.id });
      return;
    }
    if (patch === 'invalid-number') {
      setError(t('cardBack.customFields.invalidNumber'));
      return;
    }
    if (patch === 'invalid-date') {
      // ⚠ This section's OWN copy of the sentence, not the leaf's. See the key's
      // comment in `interface-language.tsx` and the catch in `patchFromDraft`.
      setError(t('cardBack.customFields.invalidDate'));
      return;
    }

    await updateValue.mutateAsync({
      cardId,
      boardId,
      fieldId: field.id,
      body: patch,
    });
  }

  async function clear(): Promise<void> {
    if (!canEdit || busy || !hasValue) return;
    setError(null);
    await clearValue.mutateAsync({ cardId, boardId, fieldId: field.id });
  }

  async function setImmediateValue(
    value: CustomFieldValuePatchBody | null,
  ): Promise<void> {
    if (!canEdit || busy) return;
    setError(null);
    if (value === null) {
      await clearValue.mutateAsync({ cardId, boardId, fieldId: field.id });
      return;
    }
    await updateValue.mutateAsync({ cardId, boardId, fieldId: field.id, body: value });
  }

  return (
    <article className="rounded-card border border-border bg-surface-raised px-3 py-2">
      <div className="flex flex-wrap items-start gap-2">
        <FieldIcon type={field.type} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-ink">{field.name}</h3>
          <p className="text-xs text-ink-muted">{fieldTypeLabel(t, field.type)}</p>
        </div>
      </div>

      <div className="mt-2">
        {canEdit ? (
          field.type === 'checkbox' ? (
            <label className="inline-flex items-center gap-2 text-sm font-medium text-ink">
              <input
                type="checkbox"
                className="size-4 rounded border-border-strong text-accent focus:ring-accent"
                checked={field.value?.type === 'checkbox' && field.value.value}
                disabled={busy}
                onChange={(event) =>
                  void setImmediateValue(
                    event.currentTarget.checked
                      ? { type: 'checkbox', value: true }
                      : null,
                  )
                }
              />
              {t('cardBack.customFields.checkedLabel')}
            </label>
          ) : field.type === 'dropdown' ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex min-w-48 flex-1 flex-col gap-1 text-sm font-medium text-ink">
                {t('cardBack.customFields.valueLabel')}
                <select
                  className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-sm text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
                  value={field.value?.type === 'dropdown' ? field.value.optionId : ''}
                  disabled={busy}
                  onChange={(event) =>
                    void setImmediateValue(
                      event.currentTarget.value === ''
                        ? null
                        : { type: 'dropdown', optionId: event.currentTarget.value },
                    )
                  }
                >
                  <option value="">{t('cardBack.customFields.unsetOption')}</option>
                  {field.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <ClearButton disabled={!hasValue || busy} onClick={() => void clear()} />
            </div>
          ) : (
            <form
              className="flex flex-col gap-2"
              onSubmit={(event) => void submit(event)}
            >
              <FieldDraftInput
                field={field}
                draft={draft}
                error={error}
                disabled={busy}
                onDraftChange={setDraft}
              />
              <div className="flex flex-wrap justify-between gap-2">
                <ClearButton disabled={!hasValue || busy} onClick={() => void clear()} />
                <Button type="submit" size="sm" variant="primary" busy={busy}>
                  {t('cardBack.customFields.save')}
                </Button>
              </div>
            </form>
          )
        ) : (
          <p
            className={cn(
              'text-card text-ink',
              !hasValue && 'text-ink-muted',
              field.type === 'text' && 'whitespace-pre-wrap',
            )}
          >
            {currentLabel}
          </p>
        )}
      </div>
    </article>
  );
}

function FieldDraftInput({
  field,
  draft,
  error,
  disabled,
  onDraftChange,
}: {
  field: CardCustomField;
  draft: string;
  error: string | null;
  disabled: boolean;
  onDraftChange: (value: string) => void;
}): ReactElement {
  // These three read the context directly rather than taking a `label` prop.
  // One string, one surface, three sibling branches of one component in one
  // file - threading it as a prop would put the same value in two more places
  // for a translator to keep in step, and the handoff's "a prop that crosses
  // three files must be named in all three" is a warning about seams, not an
  // instruction to manufacture one.
  const { t } = useInterfaceLanguage();
  const errorProps = error === null ? {} : { error };

  if (field.type === 'text') {
    return (
      <TextField
        label={t('cardBack.customFields.valueLabel')}
        value={draft}
        {...errorProps}
        multiline
        maxLength={2048}
        disabled={disabled}
        onChange={(event) => onDraftChange(event.currentTarget.value)}
      />
    );
  }

  if (field.type === 'date') {
    return (
      <TextField
        label={t('cardBack.customFields.valueLabel')}
        type="datetime-local"
        value={draft}
        {...errorProps}
        disabled={disabled}
        onChange={(event) => onDraftChange(event.currentTarget.value)}
      />
    );
  }

  return (
    <TextField
      label={t('cardBack.customFields.valueLabel')}
      type="text"
      inputMode="decimal"
      value={draft}
      {...errorProps}
      disabled={disabled}
      onChange={(event) => onDraftChange(event.currentTarget.value)}
    />
  );
}

function ClearButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <Button type="button" size="sm" variant="quiet" disabled={disabled} onClick={onClick}>
      {t('cardBack.customFields.clear')}
    </Button>
  );
}

function FieldIcon({ type }: { type: CardCustomField['type'] }): ReactElement {
  const className = 'mt-0.5 size-4 shrink-0 text-ink-muted';
  switch (type) {
    case 'text':
      return <Type aria-hidden="true" className={className} />;
    case 'number':
      return <Hash aria-hidden="true" className={className} />;
    case 'checkbox':
      return <CheckSquare aria-hidden="true" className={className} />;
    case 'date':
      return <CalendarDays aria-hidden="true" className={className} />;
    case 'dropdown':
      return <ListFilter aria-hidden="true" className={className} />;
    default:
      return <SlidersHorizontal aria-hidden="true" className={className} />;
  }
}

/**
 * ⚠ These three helpers used to type `localePreferences` as
 * `Parameters<typeof …>[1]`. That alias is positional, so inserting a
 * translator as the first parameter of a `locale-formatting` export silently
 * re-points `[1]` from `LocalePreferences` to `string` — and `tsc` then fails at
 * the *call site*, naming neither the alias nor the real cause, which makes
 * bumping the index look like the fix. Naming the type removes the trap rather
 * than dodging it once.
 */
function draftFromField(
  field: CardCustomField,
  localePreferences: LocalePreferences,
): string {
  if (field.value === null) return '';
  switch (field.value.type) {
    case 'text':
      return field.value.value;
    case 'number':
      return String(field.value.value);
    case 'date':
      return formatDateTimeLocalInZone(field.value.value, localePreferences);
    case 'checkbox':
    case 'dropdown':
      return '';
  }
}

function patchFromDraft(
  t: LocaleFormattingTranslate,
  field: CardCustomField,
  draft: string,
  localePreferences: LocalePreferences,
): CustomFieldValuePatchBody | null | 'invalid-number' | 'invalid-date' {
  const trimmed = draft.trim();
  switch (field.type) {
    case 'text':
      return trimmed.length === 0 ? null : { type: 'text', value: trimmed };
    case 'number': {
      if (trimmed.length === 0) return null;
      const value = Number(trimmed);
      return Number.isFinite(value) ? { type: 'number', value } : 'invalid-number';
    }
    case 'date':
      if (trimmed.length === 0) return null;
      try {
        return {
          type: 'date',
          value: dateTimeLocalInZoneToIso(t, trimmed, localePreferences),
        };
      } catch {
        // ⚠ The thrown message is DISCARDED here, and the catch stays bare on
        // purpose. This section re-authors the same sentence itself, at the
        // `'invalid-date'` branch of `submit`, which now routes through
        // `cardBack.customFields.invalidDate` — a THIRD key carrying that exact
        // English, beside the leaf's `primitives.localeFormatting.invalidDateTime`
        // (which `CardDatesSection` renders as `cause.message`) and the Inbox
        // panel's `inbox.card.invalidDate`. Switching this to `cause.message` to
        // collapse the three would change rendered output and hand one surface's
        // translator the wording of another's.
        return 'invalid-date';
      }
    case 'checkbox':
    case 'dropdown':
      return null;
  }
}

function valueLabel(
  t: LocaleFormattingTranslate,
  field: CardCustomField,
  localePreferences: LocalePreferences,
): string {
  // ⚠ `unsetValue` and `checkedValue`, NOT the `unsetOption`/`checkedLabel` that
  // the editable branch renders. Everything this function returns is read by
  // someone who CANNOT edit the field - it reaches the DOM only through the
  // `canEdit === false` arm of the ternary above - so these are states reported,
  // where the other two are affordances offered. The two halves can never
  // appear on the same screen, which is what makes them separable at all.
  //
  // ⚠ There is no `Unchecked`: an unticked checkbox is stored as `null` and
  // leaves through the first line of this function, not through the `checkbox`
  // case. The rendered pair is `Checked`/`Unset`.
  if (field.value === null) return t('cardBack.customFields.unsetValue');
  switch (field.value.type) {
    case 'text':
      return field.value.value;
    case 'number':
      return String(field.value.value);
    case 'checkbox':
      return t('cardBack.customFields.checkedValue');
    case 'date':
      return formatLocaleCardDate(t, field.value.value, localePreferences);
    case 'dropdown':
      return field.value.label;
  }
}

/**
 * The five nouns moved to `lib/custom-field-type.ts` and
 * `primitives.customFieldType.*` when the board-frame slice became the THIRD
 * author of the same English words — which is the threshold this docstring's
 * previous revision named. `board/BoardCustomFieldsSection.tsx` now calls the
 * same leaf; the five `cardBack.customFields.type.*` keys are gone.
 */
function fieldTypeLabel(
  t: LocaleFormattingTranslate,
  type: CardCustomField['type'],
): string {
  return customFieldTypeLabel(t, type);
}
